import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {} from "react-router"
import {AppThunk} from "../../rootReducer"
import {getAccessToken} from "../../utils/common"
import httpService from "../../utils/httpService"
import {UserDealer} from "../App/models"

export interface DashboardParams {
    selectedDealer: UserDealer | null
}

export interface BookingCountDetail {
    specification: string
    daily: string
    weekly: string
    monthly: string
}

export interface BookingTestCodeDetail {
    testCode: string
    testName: string
    totalCount: number
    totalFee: number
    testFee: number
    laneFee: number
}

export interface BookingCountVehicleTypeDetail {
    dateText: string
    dateVal: Date
    hgv: number
    psv: number
    trailer: number
    other: number
}

export interface Child {
    name: string
    children: ChildChild[]
    value: null
}

export interface ChildChild {
    name: string
    children: ChildChild[] | null
    value: number | null
}
export interface PassRateCountDetail {
    name: string
    children: Child[]
}

export interface TestTypeCountDetail {
    testPackage: string
    noOfPASS: number
    noOfPRS: number
    noOfFAIL: number
}

export interface PerformanceCountDetail {
    date: number
    confirmed: number
    cancelled: number
    noShow: number
    targetTestFee: number
    actualTestFee: number
}

export interface ProfitOrLossMonthly {
    date: number
    visits: number
}

type DashboardState = {
    dashboardFilters: DashboardParams

    bookingCountDetail: BookingCountDetail[]
    isLoadingBookingCount: boolean

    bookingCountVehicleType: BookingCountVehicleTypeDetail[] | null
    isLoadingBookingVehicleType: boolean
    passrateCountDetail: PassRateCountDetail[] | null
    isLoadingPassRateCount: boolean
    testtypeCountDetail: TestTypeCountDetail[] | null
    isLoadingTestTypeCount: boolean
    performanceCountDetail: PerformanceCountDetail[] | null
    isLoadingPerformanceCount: boolean
    profitorlossmonthlyDetail: ProfitOrLossMonthly[] | null
    isLoadingProfitLossPer: boolean
}

const initialState: DashboardState = {
    dashboardFilters: {
        selectedDealer: null,
    },
    bookingCountDetail: [],
    isLoadingBookingCount: true,
    bookingCountVehicleType: null,
    isLoadingBookingVehicleType: true,
    passrateCountDetail: null,
    isLoadingPassRateCount: true,
    testtypeCountDetail: null,
    isLoadingTestTypeCount: true,
    performanceCountDetail: null,
    isLoadingPerformanceCount: true,
    profitorlossmonthlyDetail: null,
    isLoadingProfitLossPer: true,
}

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
        setDashboardFilters(state, action: PayloadAction<DashboardParams>) {
            state.dashboardFilters = action.payload
        },
        setBookingCountDetail(state, action: PayloadAction<BookingCountDetail[]>) {
            state.bookingCountDetail = action.payload
        },
        setIsLoadingBookingCount(state, action: PayloadAction<boolean>) {
            state.isLoadingBookingCount = action.payload
        },
        setBookingCountVehicleTypeDetail(state, action: PayloadAction<BookingCountVehicleTypeDetail[] | null>) {
            state.bookingCountVehicleType = action.payload
        },
        setIsLoadingBookingVehicleType(state, action: PayloadAction<boolean>) {
            state.isLoadingBookingVehicleType = action.payload
        },
        setPassRateCountDetail(state, action: PayloadAction<PassRateCountDetail[] | null>) {
            state.passrateCountDetail = action.payload
        },
        setIsLoadingPassRateCount(state, action: PayloadAction<boolean>) {
            state.isLoadingPassRateCount = action.payload
        },
        setTestTypeCountDetail(state, action: PayloadAction<TestTypeCountDetail[] | null>) {
            state.testtypeCountDetail = action.payload
        },
        setIsLoadingTestTypeCount(state, action: PayloadAction<boolean>) {
            state.isLoadingTestTypeCount = action.payload
        },
        setPerformanceCountDetail(state, action: PayloadAction<PerformanceCountDetail[] | null>) {
            state.performanceCountDetail = action.payload
        },
        setIsLoadingPerformanceCount(state, action: PayloadAction<boolean>) {
            state.isLoadingPerformanceCount = action.payload
        },
        setProfitOrLossMonthlyDetail(state, action: PayloadAction<ProfitOrLossMonthly[] | null>) {
            state.profitorlossmonthlyDetail = action.payload
        },
        setIsLoadingProfitLossPer(state, action: PayloadAction<boolean>) {
            state.isLoadingProfitLossPer = action.payload
        },
    },
})

export const {
    setDashboardFilters,
    setBookingCountDetail,
    setIsLoadingBookingCount,
    setBookingCountVehicleTypeDetail,
    setIsLoadingBookingVehicleType,
    setPassRateCountDetail,
    setIsLoadingPassRateCount,
    setTestTypeCountDetail,
    setIsLoadingTestTypeCount,
    setPerformanceCountDetail,
    setIsLoadingPerformanceCount,
    setProfitOrLossMonthlyDetail,
    setIsLoadingProfitLossPer,
} = dashboardSlice.actions
export default dashboardSlice.reducer

export const setDashboardFiltersAction =
    (data: DashboardParams): AppThunk =>
    dispatch => {
        dispatch(setDashboardFilters(data))
    }

export const fetchBookingCountDetail =
    (param: DashboardParams): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setIsLoadingBookingCount(true))
        httpService(getAccessToken(getState()))
            .get(`regions/dashboard/booking-summary?dealerId=${param.selectedDealer?.dealerId}`)
            .then(res => {
                dispatch(setBookingCountDetail(res.data))
                dispatch(setIsLoadingBookingCount(false))
            })
            .catch(error => {
                dispatch(setBookingCountDetail([]))
                dispatch(setIsLoadingBookingCount(false))
            })
    }

export const fetchBookingCountVehicleTypeDetail =
    (param: DashboardParams): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setIsLoadingBookingVehicleType(true))
        httpService(getAccessToken(getState()))
            .get(`regions/dashboard/bookingcount-vehicletype?dealerId=${param.selectedDealer?.dealerId}`)
            .then(res => {
                dispatch(setBookingCountVehicleTypeDetail(res.data))
                dispatch(setIsLoadingBookingVehicleType(false))
            })
            .catch(error => {
                dispatch(setBookingCountVehicleTypeDetail(null))
                dispatch(setIsLoadingBookingVehicleType(false))
            })
    }

export const fetchPassRateCountDetail =
    (param: DashboardParams): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setIsLoadingPassRateCount(true))
        httpService(getAccessToken(getState()))
            .get(`regions/dashboard/passrate-count?dealerId=${param.selectedDealer?.dealerId}`)
            .then(res => {
                dispatch(setPassRateCountDetail(res.data))
                dispatch(setIsLoadingPassRateCount(false))
            })
            .catch(error => {
                dispatch(setPassRateCountDetail(null))
                dispatch(setIsLoadingPassRateCount(false))
            })
    }

export const fetchTestTypeCountDetail =
    (param: DashboardParams): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setIsLoadingTestTypeCount(true))

        httpService(getAccessToken(getState()))
            .get(`regions/dashboard/testtype-count?dealerId=${param.selectedDealer?.dealerId}`)
            .then(res => {
                dispatch(setTestTypeCountDetail(res.data))
                dispatch(setIsLoadingTestTypeCount(false))
            })
            .catch(error => {
                dispatch(setTestTypeCountDetail(null))
                dispatch(setIsLoadingTestTypeCount(false))
            })
    }

export const fetchPerformanceCountDetail =
    (param: DashboardParams): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setIsLoadingPerformanceCount(true))
        httpService(getAccessToken(getState()))
            .get(`regions/dashboard/performance-count?dealerId=${param.selectedDealer?.dealerId}`)
            .then(res => {
                const performance = res.data.map((data: PerformanceCountDetail) => ({
                    ...data,
                    date: new Date(data.date).getTime(),
                }))
                dispatch(setPerformanceCountDetail(performance))
                dispatch(setIsLoadingPerformanceCount(false))
            })
            .catch(error => {
                dispatch(setPerformanceCountDetail(null))
                dispatch(setIsLoadingPerformanceCount(false))
            })
    }

export const fetchProfitOrLossMonthlyDetail =
    (param: DashboardParams): AppThunk =>
    (dispatch: any, getState) => {
        dispatch(setIsLoadingProfitLossPer(true))
        httpService(getAccessToken(getState()))
            .get(`regions/dashboard/profit-loss-monthly?dealerId=${param.selectedDealer?.dealerId}`)
            .then(res => {
                dispatch(setProfitOrLossMonthlyDetail(res.data))
                dispatch(setIsLoadingProfitLossPer(false))
            })
            .catch(error => {
                dispatch(setProfitOrLossMonthlyDetail(null))
                dispatch(setIsLoadingProfitLossPer(false))
            })
    }
